export type Lang = "en" | "de";

export interface QuizQuestion {
  q: string;
  options: string[];
  a: number;
}

export interface Dict {
  brand: string;
  tagline: string;
  nav: {
    home: string;
    brain: string;
    network: string;
    evolution: string;
    quiz: string;
  };
  lang: string;
  sound: string;
  soundOn: string;
  soundOff: string;
  errorTitle: string;
  errorBody: string;
  errorReload: string;
  home: {
    title1: string;
    titlePop: string;
    title2: string;
    intro: string;
    pickChapter: string;
    chapters: { emoji: string; title: string; desc: string }[];
  };
  brain: {
    title: string;
    subtitle: string;
    body1: string;
    tryTitle: string;
    tryBody: string;
    reset: string;
    fireFirst: string;
    didYouKnow: string;
    next: string;
  };
  network: {
    title: string;
    subtitle: string;
    body: string;
    features: { ic: string; label: string }[];
    inputLabel: string;
    outputLabel: string;
    catYes: string;
    catNo: string;
    confidence: string;
    layerInput: string;
    layerHidden: string;
    layerOutput: string;
    scoreTitle: string;
    scoreEmpty: string;
    scoreTotal: string;
    points: string;
    catLineLabel: string;
    tipTitle: string;
    tipBody: string;
    next: string;
  };
  evolution: {
    title: string;
    subtitle: string;
    body: string;
    start: string;
    pause: string;
    reset: string;
    faster: string;
    slower: string;
    generation: string;
    best: string;
    alive: string;
    reached: string;
    banner: string;
    ghostLabel: string;
    familyTitle: string;
    familyBody: string;
    darwinTitle: string;
    darwinBody: string;
    next: string;
  };
  quiz: {
    title: string;
    subtitle: string;
    retry: string;
    score: string;
    questions: QuizQuestion[];
  };
  footer: string;
}

export const dict: Record<Lang, Dict> = {
  en: {
    brand: "Brainy Bots",
    tagline: "How AI works — for curious kids",
    nav: {
      home: "Home",
      brain: "Your Brain",
      network: "Cat Detector",
      evolution: "Evolution",
      quiz: "Quiz",
    },
    lang: "Language",
    sound: "Sound",
    soundOn: "🔊 Sound on",
    soundOff: "🔇 Sound off",
    errorTitle: "Oops! A little glitch.",
    errorBody:
      "Something went wonky inside the robot brain. Don't worry — your progress is safe. Try reloading the page!",
    errorReload: "↻ Reload",

    home: {
      title1: "How do",
      titlePop: "robots think?",
      title2: "",
      intro:
        "Hi explorer! Computers can learn — kind of like you do. Pick a chapter to find out how brains, robots, and even animals all share a SECRET superpower.",
      pickChapter: "Start with Chapter 1!",
      chapters: [
        {
          emoji: "🧠",
          title: "Your Amazing Brain",
          desc: "Meet tiny brain helpers called neurons.",
        },
        {
          emoji: "🤖",
          title: "Build a Cat Detector",
          desc: "Train a robot to spot a cat. Flip switches!",
        },
        {
          emoji: "🐛",
          title: "Evolution Race",
          desc: "Watch creatures get smarter over time.",
        },
        {
          emoji: "❓",
          title: "Quiz Time",
          desc: "Show off what you learned!",
        },
      ],
    },

    brain: {
      title: "Your Amazing Brain",
      subtitle: "It has 86 BILLION tiny helpers called NEURONS.",
      body1:
        "Each neuron is like a little messenger. When one wakes up, it taps its friends to wake them up too — like a giant game of telephone! Together, billions of these tiny taps let you think, dream, laugh, and ride a bike.",
      tryTitle: "Try it!",
      tryBody:
        "Click the first neuron. Watch the message zoom through your brain and turn on the lightbulb idea! 💡",
      reset: "Reset",
      fireFirst: "Tap the first neuron",
      didYouKnow:
        "Did you know? Computers copy this trick! They use FAKE neurons made of math — that's a Neural Network.",
      next: "Next: Cat Detector →",
    },

    network: {
      title: "Cat Detector 3000",
      subtitle:
        "Let's teach a robot to guess: is this animal a cat? Flip the switches!",
      body: "A Neural Network is layers of fake neurons. Each one looks for a small clue. Then they vote together to make a final guess.",
      features: [
        { ic: "👀", label: "Has whiskers?" },
        { ic: "🐈", label: "Has a long tail?" },
        { ic: "🔊", label: 'Says "meow"?' },
        { ic: "🪶", label: "Has feathers?" },
      ],
      inputLabel: "What does the animal have?",
      outputLabel: "Robot guesses:",
      catYes: "It's probably a CAT! 🐱",
      catNo: "Hmm... not a cat. 🤔",
      confidence: "Sure-o-meter",
      layerInput: "Input",
      layerHidden: "Hidden",
      layerOutput: "Output",
      scoreTitle: "Cat-points scoreboard",
      scoreEmpty: "Flip a switch to give the robot some clues!",
      scoreTotal: "Total",
      points: "cat points",
      catLineLabel: "Above 0 = it's a cat!",
      tipTitle: "How it works",
      tipBody:
        "Each clue is worth some CAT POINTS — that's its WEIGHT. Whiskers = +3 (pretty cat-ish). Meow = +5 (super cat-ish). Feathers = −7 (NOT a cat at all!). The robot just adds them up. If the total is above 0, it shouts CAT!",
      next: "Next: Evolution →",
    },

    evolution: {
      title: "Evolution Race",
      subtitle:
        "How do AIs (and animals!) get better? They try, they fail, and the best ones make babies.",
      body: "Watch these little bugs try to reach the carrot 🥕. The fastest bugs survive and pass their DNA to the next generation — with tiny random changes (mutations). After many tries, they get smarter and smarter!",
      start: "Start Race",
      pause: "Pause",
      reset: "New Race",
      faster: "Faster",
      slower: "Slower",
      generation: "Generation",
      best: "Best score",
      alive: "Alive",
      reached: "Reached carrot",
      banner: "🏆 These got CLOSEST to the carrot — their babies will copy their tricks!",
      ghostLabel: "👻 Last champion's path",
      familyTitle: "Just like in your family! 👨‍👩‍👧",
      familyBody:
        "The bugs that got CLOSEST to the carrot are the winners. They become moms and dads — and their babies are born already knowing most of mommy's and daddy's tricks, just like how YOU might have your mom's curly hair or your dad's freckles. Each baby also gets one tiny random twist (a MUTATION) — like a brand-new freckle nobody else has. The babies that are even better at finding the carrot become the next moms and dads. After a few rounds, the whole family is great at it!",
      darwinTitle: "Real science!",
      darwinBody:
        "A long time ago, a scientist named Charles Darwin noticed that animals with helpful tricks lived longer and had more babies. Over millions of years, this is how giraffes got long necks and cheetahs got fast legs!",
      next: "Next: Quiz →",
    },

    quiz: {
      title: "Quiz Time!",
      subtitle: "Pick the best answer. Don't worry — you can try again!",
      retry: "Try again",
      score: "Your score:",
      questions: [
        {
          q: "What is a neuron?",
          options: [
            "A tiny helper in your brain that passes messages",
            "A type of star in space",
            "A small lizard",
          ],
          a: 0,
        },
        {
          q: "What is a Neural Network?",
          options: [
            "A bunch of TVs glued together",
            "Layers of fake neurons that learn to spot things",
            "A spider's web",
          ],
          a: 1,
        },
        {
          q: "How does an AI get better?",
          options: [
            "It eats lots of cookies",
            "It tries, fails, and learns from mistakes",
            "It watches cartoons all day",
          ],
          a: 1,
        },
        {
          q: "What does 'survival of the fittest' mean?",
          options: [
            "Only the strongest animals do push-ups",
            "Animals best suited to a place have more babies",
            "Fast animals always win races",
          ],
          a: 1,
        },
        {
          q: "What is a 'mutation' in evolution?",
          options: [
            "A small random change in DNA",
            "A superhero power",
            "A type of pizza",
          ],
          a: 0,
        },
      ],
    },

    footer: "Made with curiosity 💛  — for explorers age 8 and up.",
  },

  de: {
    brand: "Schlaue Bots",
    tagline: "Wie KI funktioniert — für neugierige Kinder",
    nav: {
      home: "Start",
      brain: "Dein Gehirn",
      network: "Katzen-Detektor",
      evolution: "Evolution",
      quiz: "Quiz",
    },
    lang: "Sprache",
    sound: "Ton",
    soundOn: "🔊 Ton an",
    soundOff: "🔇 Ton aus",
    errorTitle: "Hoppla! Ein kleiner Fehler.",
    errorBody:
      "Im Roboter-Gehirn ist etwas schiefgelaufen. Keine Sorge — dein Fortschritt ist sicher. Lade die Seite einfach neu!",
    errorReload: "↻ Neu laden",

    home: {
      title1: "Wie",
      titlePop: "denken Roboter?",
      title2: "",
      intro:
        "Hallo Entdecker! Computer können lernen — fast so wie du. Wähle ein Kapitel und finde heraus, welche geheime Superkraft Gehirne, Roboter und sogar Tiere teilen.",
      pickChapter: "Starte mit Kapitel 1!",
      chapters: [
        {
          emoji: "🧠",
          title: "Dein tolles Gehirn",
          desc: "Lerne winzige Gehirnhelfer kennen: Neuronen.",
        },
        {
          emoji: "🤖",
          title: "Baue einen Katzen-Detektor",
          desc: "Bring einem Roboter bei, Katzen zu erkennen!",
        },
        {
          emoji: "🐛",
          title: "Evolutions-Rennen",
          desc: "Schau zu, wie Tierchen immer schlauer werden.",
        },
        {
          emoji: "❓",
          title: "Quiz",
          desc: "Zeig, was du gelernt hast!",
        },
      ],
    },

    brain: {
      title: "Dein tolles Gehirn",
      subtitle: "Es hat 86 MILLIARDEN winzige Helfer — sie heißen NEURONEN.",
      body1:
        "Jedes Neuron ist ein kleiner Bote. Wenn eins aufwacht, tippt es seinen Freunden auf die Schulter, damit sie auch aufwachen — wie eine riesige Stille Post! Zusammen lassen dich Milliarden dieser kleinen Tipper denken, träumen, lachen und Fahrrad fahren.",
      tryTitle: "Probier es aus!",
      tryBody:
        "Tippe auf das erste Neuron. Schau zu, wie die Nachricht durch dein Gehirn flitzt und die Glühbirne anschaltet! 💡",
      reset: "Zurücksetzen",
      fireFirst: "Tippe auf das erste Neuron",
      didYouKnow:
        "Wusstest du? Computer kopieren diesen Trick! Sie benutzen NACHGEMACHTE Neuronen aus Mathe — das nennt man Neuronales Netz.",
      next: "Weiter: Katzen-Detektor →",
    },

    network: {
      title: "Katzen-Detektor 3000",
      subtitle:
        "Bringen wir einem Roboter bei zu raten: Ist das Tier eine Katze? Schalter umlegen!",
      body: "Ein Neuronales Netz besteht aus Schichten von nachgemachten Neuronen. Jedes sucht nach einem kleinen Hinweis. Dann stimmen sie ab und entscheiden gemeinsam.",
      features: [
        { ic: "👀", label: "Hat Schnurrhaare?" },
        { ic: "🐈", label: "Hat einen langen Schwanz?" },
        { ic: "🔊", label: 'Sagt „Miau"?' },
        { ic: "🪶", label: "Hat Federn?" },
      ],
      inputLabel: "Was hat das Tier?",
      outputLabel: "Roboter rät:",
      catYes: "Das ist wahrscheinlich eine KATZE! 🐱",
      catNo: "Hmm... keine Katze. 🤔",
      confidence: "Sicher-o-meter",
      layerInput: "Eingabe",
      layerHidden: "Versteckt",
      layerOutput: "Ausgabe",
      scoreTitle: "Katzenpunkte-Tafel",
      scoreEmpty: "Leg einen Schalter um, damit der Roboter Hinweise bekommt!",
      scoreTotal: "Gesamt",
      points: "Katzenpunkte",
      catLineLabel: "Über 0 = es ist eine Katze!",
      tipTitle: "So funktioniert's",
      tipBody:
        "Jeder Hinweis ist eine bestimmte Anzahl KATZENPUNKTE wert — das ist sein GEWICHT. Schnurrhaare = +3 (ziemlich katzig). Miau = +5 (super katzig). Federn = −7 (gar keine Katze!). Der Roboter zählt einfach alles zusammen. Liegt das Ergebnis über 0, ruft er: KATZE!",
      next: "Weiter: Evolution →",
    },

    evolution: {
      title: "Evolutions-Rennen",
      subtitle:
        "Wie werden KIs (und Tiere!) besser? Sie probieren, scheitern, und die Besten bekommen Babys.",
      body: "Schau zu, wie die kleinen Käfer versuchen, zur Karotte 🥕 zu kommen. Die schnellsten Käfer überleben und geben ihre DNA an die nächste Generation weiter — mit kleinen zufälligen Veränderungen (Mutationen). Nach vielen Versuchen werden sie schlauer und schlauer!",
      start: "Rennen starten",
      pause: "Pause",
      reset: "Neues Rennen",
      faster: "Schneller",
      slower: "Langsamer",
      generation: "Generation",
      best: "Bestes Ergebnis",
      alive: "Lebende",
      reached: "Karotte erreicht",
      banner: "🏆 Diese kamen der Karotte am NÄCHSTEN — ihre Babys übernehmen ihre Tricks!",
      ghostLabel: "👻 Pfad des letzten Champions",
      familyTitle: "Genau wie in deiner Familie! 👨‍👩‍👧",
      familyBody:
        "Die Käfer, die der Karotte am NÄCHSTEN gekommen sind, sind die Gewinner. Sie werden Mamas und Papas — und ihre Babys kommen schon mit fast den gleichen Tricks zur Welt, so wie du vielleicht Mamas Locken oder Papas Sommersprossen hast. Jedes Baby bekommt außerdem eine winzige zufällige Veränderung (eine MUTATION) — wie eine brandneue Sommersprosse, die sonst niemand hat. Die Babys, die noch besser darin sind, die Karotte zu finden, werden die nächsten Mamas und Papas. Nach ein paar Runden ist die ganze Familie richtig gut darin!",
      darwinTitle: "Echte Wissenschaft!",
      darwinBody:
        "Vor langer Zeit bemerkte ein Forscher namens Charles Darwin: Tiere mit nützlichen Tricks lebten länger und hatten mehr Babys. Über Millionen Jahre bekamen so Giraffen lange Hälse und Geparden schnelle Beine!",
      next: "Weiter: Quiz →",
    },

    quiz: {
      title: "Quiz!",
      subtitle:
        "Wähle die beste Antwort. Keine Sorge — du darfst nochmal probieren!",
      retry: "Nochmal versuchen",
      score: "Dein Ergebnis:",
      questions: [
        {
          q: "Was ist ein Neuron?",
          options: [
            "Ein winziger Helfer in deinem Gehirn, der Nachrichten weitergibt",
            "Ein Stern im Weltall",
            "Eine kleine Echse",
          ],
          a: 0,
        },
        {
          q: "Was ist ein Neuronales Netz?",
          options: [
            "Ein Haufen Fernseher, die zusammengeklebt sind",
            "Schichten aus nachgemachten Neuronen, die lernen, Dinge zu erkennen",
            "Ein Spinnennetz",
          ],
          a: 1,
        },
        {
          q: "Wie wird eine KI besser?",
          options: [
            "Sie isst viele Kekse",
            "Sie probiert, scheitert und lernt aus Fehlern",
            "Sie schaut den ganzen Tag Zeichentrick",
          ],
          a: 1,
        },
        {
          q: 'Was bedeutet „Überleben der Stärksten"?',
          options: [
            "Nur die stärksten Tiere machen Liegestütze",
            "Tiere, die gut zu ihrem Lebensraum passen, haben mehr Babys",
            "Schnelle Tiere gewinnen immer Rennen",
          ],
          a: 1,
        },
        {
          q: 'Was ist eine „Mutation" in der Evolution?',
          options: [
            "Eine kleine zufällige Veränderung in der DNA",
            "Eine Superhelden-Kraft",
            "Eine Pizzasorte",
          ],
          a: 0,
        },
      ],
    },

    footer: "Mit Neugier gemacht 💛  — für Entdecker ab 8 Jahren.",
  },
};
